import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  BookOpenIcon, ArrowsRightLeftIcon, ExclamationTriangleIcon, ClockIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { createPortalStaffPages } from './PortalStaffPortal';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tooltip">
      <div className="ct-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="ct-row">
          <div className="ct-dot" style={{ background: p.color }} />
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

const CAT_COLORS = ['#f59e0b', '#3b6ef8', '#10c98e', '#ef4444', '#a855f7', '#14b8a6', '#f97316'];

// Rendered inside the Librarian Dashboard (via PortalStaffPortal's DashboardExtra
// slot) — the same overview stats Admin/Finance see in LibraryOverview.jsx, plus
// two chart panels, so the librarian's own dashboard isn't just quick links.
const LibraryDashboardStats = ({ apiBase }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`${apiBase}/dashboard-stats`).then(r => setData(r.data)).catch(()=>{}); }, [apiBase]);
  if (!data) return null;
  const { stats, categoryBreakdown, issuesTrend } = data;

  return (
    <>
      <div className="stat-grid" style={{gridTemplateColumns:'repeat(5,1fr)'}}>
        <div className="stat-card"><div className="s-icon amber"><BookOpenIcon/></div><div className="s-label">Books / Copies</div><div className="s-value">{stats.totalBooks} / {stats.totalCopies}</div></div>
        <div className="stat-card"><div className="s-icon blue"><ArrowsRightLeftIcon/></div><div className="s-label">Issued Out</div><div className="s-value">{stats.issuedOut}</div></div>
        <div className="stat-card"><div className="s-icon red"><ExclamationTriangleIcon/></div><div className="s-label">Overdue</div><div className="s-value">{stats.overdueCount}</div></div>
        <div className="stat-card"><div className="s-icon amber"><ClockIcon/></div><div className="s-label">Pending Fines</div><div className="s-value">{stats.pendingFines}</div></div>
        <div className="stat-card"><div className="s-icon green"><CheckCircleIcon/></div><div className="s-label">Fines Total</div><div className="s-value" style={{fontSize:'1.1rem'}}>Rs. {Number(stats.finesTotal).toLocaleString()}</div></div>
      </div>
      <div className="charts-row">
        <div className="chart-panel">
          <div className="chart-title">Books by Category</div>
          <div className="chart-sub">Titles in your catalog, grouped by category</div>
          {categoryBreakdown.length === 0 ? <div className="chart-empty">No books yet.</div> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={categoryBreakdown} barSize={22}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f7" vertical={false} />
                <XAxis dataKey="category" tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Books" radius={[4,4,0,0]}>
                  {categoryBreakdown.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="chart-panel">
          <div className="chart-title">Issues — Last 7 Days</div>
          <div className="chart-sub">Books issued out per day</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={issuesTrend} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f7" vertical={false} />
              <XAxis dataKey="label" tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} width={28} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Issued" fill="#3b6ef8" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

const pages = createPortalStaffPages({
  apiBase: '/librarian',
  roleLabel: 'Library',
  accent: '#f59e0b',
  DashboardExtra: LibraryDashboardStats,
  quickLinks: [
    { label: 'Book Catalog',   href: '/librarian/catalog' },
    { label: 'Issue / Return', href: '/librarian/issue-return' },
    { label: 'Fines',          href: '/librarian/fines' },
    { label: 'My Attendance',  href: '/librarian/own-attendance' },
    { label: 'My Leaves',      href: '/librarian/own-leaves' },
    { label: 'My Salary',      href: '/librarian/salary' },
  ],
});

export const LibrarianDashboard    = pages.Dashboard;
export const LibrarianProfile      = pages.Profile;
export const LibrarianMyAttendance = pages.MyAttendance;
export const LibrarianMyLeaves     = pages.MyLeaves;
export const LibrarianMySalary     = pages.MySalary;
